/**
 * Doc placement mode — the same FigJam-style toggle / ghost / click-to-place
 * flow as `useTextNotePlacement`, except the click mints an empty markdown
 * file record via `useCreateDocOnCanvas` instead of adding a local node.
 *
 * Unlike the note hook, the ghost is seeded immediately from the last known
 * cursor position on mode entry and then tracked via a window-level
 * mousemove (the `useTextLabelPlacement` pattern). Pane-level mousemove
 * alone would leave a dead gap between activating the tool (toolbar flyout,
 * `D` key) and the pointer reaching open pane space.
 */

import { useReactFlow } from "@xyflow/react";
import { useCallback, useEffect, useState } from "react";
import { CANVAS_SNAP, snapToGrid } from "../canvas-controls.config";
import { useCanvasUi } from "../context/CanvasUiContext";
import {
  DOC_NODE_HEIGHT,
  DOC_NODE_WIDTH,
  useCreateDocOnCanvas,
} from "./useCreateDocOnCanvas";

interface Position {
  x: number;
  y: number;
}

export interface UseDocPlacementParams {
  /** Last known cursor position over the canvas wrapper (screen coords). */
  lastCursorScreenPosRef: React.RefObject<Position | null>;
}

export function useDocPlacement({
  lastCursorScreenPosRef,
}: UseDocPlacementParams) {
  const { screenToFlowPosition } = useReactFlow();
  const { reactFlowWrapperRef } = useCanvasUi();
  const createDoc = useCreateDocOnCanvas();
  const [isPlacingDoc, setIsPlacingDoc] = useState(false);
  const [ghostPosition, setGhostPosition] = useState<Position | null>(null);

  // Grid-snapped node position centered under the given screen coords.
  const placementFromScreenPos = useCallback(
    (screenPos: Position): Position => {
      const position = screenToFlowPosition(screenPos);
      const [gridX, gridY] = CANVAS_SNAP.grid;
      return {
        x: snapToGrid(position.x - DOC_NODE_WIDTH / 2, gridX),
        y: snapToGrid(position.y - DOC_NODE_HEIGHT / 2, gridY),
      };
    },
    [screenToFlowPosition],
  );

  const seedGhostFromScreenPos = useCallback(
    (screenPos: Position | null | undefined) => {
      if (!screenPos) {
        setGhostPosition(null);
        return;
      }
      lastCursorScreenPosRef.current = screenPos;
      setGhostPosition(placementFromScreenPos(screenPos));
    },
    [lastCursorScreenPosRef, placementFromScreenPos],
  );

  /** Always enter doc placement (toolbar / flyout). Does not toggle off. */
  const startDocPlacement = useCallback(
    (screenPos?: Position | null) => {
      setIsPlacingDoc(true);
      seedGhostFromScreenPos(screenPos ?? lastCursorScreenPosRef.current);
    },
    [lastCursorScreenPosRef, seedGhostFromScreenPos],
  );

  const handleToggleDocMode = useCallback(() => {
    setIsPlacingDoc((prev) => {
      const next = !prev;
      const cursor = lastCursorScreenPosRef.current;
      setGhostPosition(next && cursor ? placementFromScreenPos(cursor) : null);
      return next;
    });
  }, [lastCursorScreenPosRef, placementFromScreenPos]);

  // Escape key to cancel placement
  useEffect(() => {
    if (!isPlacingDoc) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsPlacingDoc(false);
        setGhostPosition(null);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isPlacingDoc]);

  // Track the cursor at the window level while placing so the ghost follows
  // from the very first move — over the toolbar, nodes, and pane alike —
  // instead of only once the pointer reaches empty pane space. Moves outside
  // the canvas wrapper (e.g. over the right pane) are ignored.
  useEffect(() => {
    if (!isPlacingDoc) return;

    const handler = (e: MouseEvent) => {
      const wrapper = reactFlowWrapperRef.current;
      if (wrapper && !wrapper.contains(e.target as globalThis.Node)) return;
      const screenPos = { x: e.clientX, y: e.clientY };
      lastCursorScreenPosRef.current = screenPos;
      setGhostPosition(placementFromScreenPos(screenPos));
    };

    window.addEventListener("mousemove", handler);

    const cursor = lastCursorScreenPosRef.current;
    if (cursor) {
      setGhostPosition(placementFromScreenPos(cursor));
    }

    return () => window.removeEventListener("mousemove", handler);
  }, [
    isPlacingDoc,
    reactFlowWrapperRef,
    lastCursorScreenPosRef,
    placementFromScreenPos,
  ]);

  // Create the doc on click
  const handlePaneClick = useCallback(
    (event: React.MouseEvent) => {
      if (!isPlacingDoc) return;
      createDoc(placementFromScreenPos({ x: event.clientX, y: event.clientY }));
      setIsPlacingDoc(false);
      setGhostPosition(null);
    },
    [isPlacingDoc, createDoc, placementFromScreenPos],
  );

  return {
    isPlacingDoc,
    setIsPlacingDoc,
    ghostPosition,
    startDocPlacement,
    handleToggleDocMode,
    handlePaneClick,
  };
}
